import React from "react";
import type { EmployeeScore } from "../modules/points/types";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import SparkChart from "@/components/SparkChart";
import { cn } from "@/lib/utils";
import { formatRelativeDay } from "@/utils/formatDate";

type EmployeeScoreCardProps = {
  employeeScore: EmployeeScore;
  preview?: boolean;
  globalMax?: number;
  averagePoints?: number;
};

function getPointsColor(points: number): string {
  if (points > 0) return "text-neutral-900";
  return "text-muted-foreground";
}

function getPointsBgColor(points: number): string {
  if (points > 0) return "bg-highlight";
  return "bg-muted";
}

const EmployeeScoreCard: React.FC<EmployeeScoreCardProps> = ({
  employeeScore,
  preview = false,
  globalMax,
}) => {
  const { latestPoints, recentPointsCalculations } = employeeScore;
  const hasPoints = latestPoints !== null;

  return (
    <Card className="py-4">
      <div className="flex flex-wrap items-center gap-3 px-6">
        <div className="min-w-0 flex-1 order-1">
          <Text
            as="div"
            variant="md"
            className="font-semibold leading-none truncate"
          >
            {employeeScore.name}
          </Text>
          <Text
            as="div"
            variant="sm"
            className="text-muted-foreground mt-1 truncate"
          >
            {employeeScore.email}
          </Text>
        </div>

        {!preview && recentPointsCalculations.length > 1 && (
          <div className="shrink-0 order-3 w-full flex justify-end md:order-2 md:w-auto md:justify-start">
            <SparkChart periods={recentPointsCalculations} globalMax={globalMax} />
          </div>
        )}

        <div className="shrink-0 order-2 md:order-3 flex flex-col items-center gap-0.5">
          <div
            className={cn(
              'flex items-center justify-center rounded-md px-3 py-1',
              hasPoints ? getPointsBgColor(latestPoints) : 'bg-muted',
            )}
          >
            <Text
              as="span"
              className={cn(
                'text-lg font-bold tabular-nums',
                hasPoints ? getPointsColor(latestPoints) : 'text-muted-foreground',
              )}
            >
              {hasPoints ? latestPoints : '—'}
            </Text>
          </div>
          {hasPoints && recentPointsCalculations.length > 0 && (
            <Text
              as="span"
              variant="xs"
              className="text-muted-foreground"
            >
              {formatRelativeDay(recentPointsCalculations[recentPointsCalculations.length - 1].calculatedOn)}
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
};

export default EmployeeScoreCard;
